export const ID_NAMESPACE = 'res';

// REVIEW: Couldn't a result include more than one type? Like IMPOSSIBLE_TRAVEL + VPN_SWITCH.
export const RESULT_TYPES = {
  IP_SIMILARITY: 'IP_SIMILARITY', // User is on a significantly different IP address than we've seen.
  IMPOSSIBLE_TRAVEL: 'IMPOSSIBLE_TRAVEL', // User is at a location they couldn't have traveled to.
  NEW_DEVICE: 'NEW_DEVICE', // User is on a device we've never seen before.
  NEW_LOCATION: 'NEW_LOCATION', // User is at a location they've never been at before (minimum distance).
  VPN_SWITCH: 'VPN_SWITCH', // User is using a VPN provider that they haven't used before.
  TOR_SWITCH: 'TOR_SWITCH', // User was not on Tor, now is.
  CARRIER_SWITCH: 'CARRIER_SWITCH', // If on mobile carrier network, on one we haven't seen.
  BAD_ACTOR: 'BAD_ACTOR', // One of known bad actors.
  COMPROMISED_PASSWORD: 'COMPROMISED_PASSWORD', // User was found using a known compromised password.
  FAILED_LOGINS_EXCEEDED: 'FAILED_LOGINS_EXCEEDED', // User has exceeded the max failed logins attempt.
  CRITICAL_ACCOUNT_CHANGE: 'CRITICAL_ACCOUNT_CHANGE', // Recovery info, forwarding rules, or admin privilege changed (Aug 5).
  EXTERNAL_DRIVE_SHARE: 'EXTERNAL_DRIVE_SHARE', // A file was shared externally or made public on the web (Aug 5, Gate 2A).
  OAUTH_PERMISSION_DRIFT: 'OAUTH_PERMISSION_DRIFT', // First-ever OAuth grant to a given app for this identity (Aug 5, Gate 2B).
  OAUTH_TOKEN_USAGE_ANOMALY: 'OAUTH_TOKEN_USAGE_ANOMALY', // An already-granted token used with anomalous data volume — possible hijacked-token exfiltration (Aug 5, Gate 2B).
  TOKEN_TAKEOVER: 'TOKEN_TAKEOVER', // Provider-flagged session/token hijack indicator (Aug 5, Gate 2B) — provider-namespaced, Google first.
  SUSPICIOUS_CALENDAR_INVITE: 'SUSPICIOUS_CALENDAR_INVITE', // Event created via an emailed .ics attachment — the classic malicious calendar invite mechanism (Aug 5).
  CALENDAR_PRIVILEGE_CHANGE: 'CALENDAR_PRIVILEGE_CHANGE', // Event ownership transferred, or high-level calendar access granted (Aug 5).
  POSTURE_MFA_NOT_ENABLED: 'POSTURE_MFA_NOT_ENABLED', // Identity Posture Audit: user has no 2-step verification enrolled.
  POSTURE_DORMANT_ACCOUNT: 'POSTURE_DORMANT_ACCOUNT', // Identity Posture Audit: active account, no login in configured threshold.
  POSTURE_MISSING_RECOVERY_INFO: 'POSTURE_MISSING_RECOVERY_INFO', // Identity Posture Audit: no recovery email or phone set.
  POSTURE_BREACHED_EMAIL: 'POSTURE_BREACHED_EMAIL', // Identity Posture Audit: email found in a known breach via HIBP.
  PASSWORD_CHANGE_NUDGE: 'PASSWORD_CHANGE_NUDGE', // Proactive recommendation, not a detected fact — Google doesn't expose password age.
  USER_RESPONSE: 'USER_RESPONSE' // User has responded to a notification using a DFend embedded link.
} as const;

// REVIEW: Actual use, usefulness, applicability of levels.
export const LEVEL_TYPES = {
  INFO: 'INFO',
  WARNING: 'WARNING',
  CRITICAL: 'CRITICAL' // Perhaps critical would relate to USER_RESPONSE type.
} as const;

// Gate 2B (Aug 5): Incident Severity axis, per the locked scoring spec.
// Kept separate from LEVEL_TYPES above rather than replacing it — `level`
// stays as-is for backward compatibility with existing code paths; this is
// the new, more deliberate 4-band CVSS-style axis. No "None" band: if
// nothing fired, there's no Result to attach a severity to.
export const SEVERITY_TYPES = {
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH',
  CRITICAL: 'CRITICAL'
} as const;

export const RECOMMENDATION_TYPES = {
  TERMINATE_SESSION: 'TERMINATE_SESSION',
  NOTIFY_USER: 'NOTIFY_USER',
  NOTIFY_USER_WITH_LINK: 'NOTIFY_USER_WITH_LINK',
  RESET_PASSWORD: 'RESET_PASSWORD',
  FLAG_FOR_NEXT: 'FLAG_FOR_NEXT',
  BAN_IP: 'BAN_IP'
} as const;

export type RESULT_TYPES_KEYS = keyof typeof RESULT_TYPES;
export type LEVEL_TYPES_KEYS = keyof typeof LEVEL_TYPES;
export type SEVERITY_TYPES_KEYS = keyof typeof SEVERITY_TYPES;
export type RECOMMENDATION_TYPES_KEYS = keyof typeof RECOMMENDATION_TYPES;

export const RESULT_TYPES_REGEX = new RegExp(Object.values(RESULT_TYPES).join('|'));
export const LEVEL_TYPES_REGEX = new RegExp(Object.values(LEVEL_TYPES).join('|'));
export const SEVERITY_TYPES_REGEX = new RegExp(Object.values(SEVERITY_TYPES).join('|'));
export const RECOMMENDATION_TYPES_REGEX = new RegExp(
  Object.values(RECOMMENDATION_TYPES).join('|')
);
